import React, { useState, useEffect } from 'react';
import Table from './Table';
import { createCSV } from '../../../utilities/general';
import ReactPaginate from 'react-paginate';
import '../../../scss/partials/shared/tables/reportsTableStyles.css';
import DownloadIcon from '../../../assets/img/DownloadIcon.png';
import '../../../assets/icons/SearchIcon.svg'


export function ReportsTable(props) {
  const { initialValue, tableHeadings, tableData, csvHeaders, csvData, csvTitle, tableStyles, sortParam, dataTest, isDescending, onHeaderClick } = props;
  const [entriesPerPage, setEntriesPerPage] = useState(initialValue);
  const [searchFilter, setSearchFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [processedData, setProcessedData] = useState([]);

  // Process data when sort/filter parameters change
  useEffect(() => {
    const searchableColumn = [];
    tableHeadings.forEach((columnheading) => {
      if(columnheading.isSortable)
        searchableColumn.push(columnheading.sortBy);
    });

    // Filter data based on search
    let searchedData = [];
    if (searchFilter !== '') {
      const doubleQuotes = new RegExp('^"(.*)"$');
      searchedData = tableData.filter((item) => {
        let filterResult = false;
        if(doubleQuotes.test(searchFilter)){
          let dblQuotesRemovedText = searchFilter.replace(/^"(.*)"$/, '$1');
          let escapedText = dblQuotesRemovedText.replace(/[\\[.+*?(){|^$]/g, "\\$&");
          let regexString = new RegExp('\\b'+ escapedText +'\\b','gi');
          searchableColumn.forEach((column) => {
            if(item[column] && item[column].sortBy && regexString.test(item[column].sortBy)){
              filterResult = true;
            }else{
              filterResult = filterResult || false;
            } 
          });
        } else {
          searchableColumn.forEach((column) => {
            if(item[column] && item[column].sortBy && item[column].sortBy.toUpperCase().includes(searchFilter.toUpperCase())){
              filterResult = true;
            }else{
              filterResult = filterResult || false;
            } 
          }); 
        }
        return filterResult;
      });
    } else {
      searchedData = tableData;
    }

    // Sort the entire filtered dataset
    const sortedData = [...searchedData].sort((a, b) => {
      if (sortParam === 'LogTime' && a.originalTimestamp && b.originalTimestamp) {
        return isDescending ? b.originalTimestamp - a.originalTimestamp : a.originalTimestamp - b.originalTimestamp; 
      }
      
      const valA = a[sortParam]?.sortBy?.toUpperCase() || '';
      const valB = b[sortParam]?.sortBy?.toUpperCase() || '';
      
      if (valA > valB) return isDescending ? -1 : 1;
      if (valA < valB) return isDescending ? 1 : -1;
      return 0;
    });

    setProcessedData(sortedData);
    setCurrentPage(0); // Reset to first page when data changes
  }, [tableData, searchFilter, sortParam, isDescending, entriesPerPage]);

  // Override Table component's header click handling
  useEffect(() => {
    const setupClickHandlers = () => {
      const headerButtons = document.querySelectorAll('div[role="columnheader"] button');
      
      if (headerButtons.length >= 3) {
        const columnMap = ['LogTime', 'LoggedBy', 'LogMessage'];
        
        headerButtons.forEach((button, index) => {
          if (index < 3) {
            // Clone to remove existing listeners
            const newButton = button.cloneNode(true);
            button.parentNode.replaceChild(newButton, button);
            
            // Add our click listener
            newButton.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();
              
              if (onHeaderClick) {
                onHeaderClick(columnMap[index]);
              }
            });
            
            newButton.style.cursor = 'pointer';
          }
        });
        
        return true;
      }
      
      return false;
    };
    
    const timer = setTimeout(() => {
      setupClickHandlers();
    }, 500);
    
    return () => clearTimeout(timer);
  }, [processedData, onHeaderClick]);

  // Calculate pagination values
  const totalLength = processedData.length;
  const totalPages = Math.ceil(totalLength / entriesPerPage);
  const startValue = currentPage * entriesPerPage;
  const endValue = Math.min(startValue + entriesPerPage, totalLength);

  function handleEntriesPerPage(event){
    const newEntriesPerPage = parseInt(event.target.value, 10);
    setEntriesPerPage(newEntriesPerPage);
    
    // Adjust current page to maintain relative position
    const newCurrentPage = Math.floor((currentPage * entriesPerPage) / newEntriesPerPage);
    setCurrentPage(newCurrentPage);
  }
 
  function handlePageChange({selected}){
    setCurrentPage(selected);
  }

  const handleSearchFilter = (event) => {
    const value = event.target.value;
    setCurrentPage(0);
    setSearchFilter(value);
  };

  const displayTable = () => {
    const pagedData = processedData.slice(startValue, endValue);
    
    // Neutralize data to prevent Table's internal sorting
    const neutralizedData = pagedData.map((item, index) => {
      const neutralized = {};
      Object.keys(item).forEach(key => {
        neutralized[key] = {
          sortBy: `${index}`,
          display: item[key].display,
          ...(item[key].originalTimestamp && { originalTimestamp: item[key].originalTimestamp })
        };
      });
      return neutralized;
    });
    
    const table = (
      <Table
        hasTableHead={true}
        hasSelector={false}
        tableHeadings={tableHeadings}
        sortParam={sortParam}
        tableData={neutralizedData}
        tableStyles={tableStyles}
        data-test={dataTest}
      />
    );
    return table;
  }

  function triggerCSV() {
    return createCSV(csvHeaders, csvData, csvTitle);
  }

  return (
    <React.Fragment>
      <div className="table-header padding-left-top-right-5 width-100 margin-bottom-0 flex-end">
        <div className="vertical-center">
          <input
            type="text"
            id="searchFilter"
            name="searchFilter"
            placeholder="Search"
            onChange={handleSearchFilter}
            className="message-table__filter-search-box search_bg"
            value={searchFilter}
          />
        </div>
        <div className="export-button vertical-center margin-right-5 margin-left-5">
          <img src={DownloadIcon} onClick={triggerCSV} alt="downloadIcon"></img>
        </div>
      </div>
      
      <div className="width-100 flex-spread padding-5 table-header flex-spread">
        <label className="text-medium text-bold" htmlFor="usersFilter">
          Show
          <select value={entriesPerPage} onChange={handleEntriesPerPage} name="tblAuditLog_length" className="margin-left-10" data-test="entries-per-page">
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={2500}>2500</option>
          </select> entries
        </label>
        
        {totalLength > 0 && (
          <div className="react-paginate" data-test="react-paginate">
            <ReactPaginate 
              previousLabel={'Prev'}
              nextLabel={'Next'}
              breakLabel={'...'}
              pageCount={totalPages}
              marginPagesDisplayed={1}
              pageRangeDisplayed={2}
              onPageChange={handlePageChange}
              activeClassName={'active'}
              forcePage={currentPage}
            />
          </div>
        )}
      </div>
      
      {displayTable()}
      
      <div className="container_2-column margin-top-10">
        {totalLength > 0 && (
          <div className="text-large placeend">
            <strong>{startValue + 1}</strong> - <strong>{endValue}</strong> of <strong>{totalLength}</strong>
          </div>
        )}
        
        {totalLength > 0 && (
          <div className="react-paginate placeend" data-test="react-paginate">
            <ReactPaginate 
              previousLabel={'Prev'}
              nextLabel={'Next'}
              breakLabel={'...'}
              pageCount={totalPages}
              marginPagesDisplayed={1}
              pageRangeDisplayed={2}
              onPageChange={handlePageChange}
              activeClassName={'active'}
              forcePage={currentPage}
            />
          </div>
        )}
      </div>
    </React.Fragment>
  );
}